import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import axios from "axios";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { HiOutlinePlusCircle, HiOutlineTrash, HiOutlineArrowPath, HiOutlineMagnifyingGlass } from "react-icons/hi2";

const libraries = ["places"];

const CompletarApartado = () => {
  const navigate = useNavigate();
  const { id, method } = useParams();
  const [reserva, setReserva] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const autocompleteRef = useRef(null);

  const [ticketBusqueda, setTicketBusqueda] = useState("");
  const [buscandoTicket, setBuscandoTicket] = useState(false);
  const [zohoTicket, setZohoTicket] = useState(null);
  const [zohoTicketId, setZohoTicketId] = useState(null);

  const [formData, setFormData] = useState({
    status: "", empresa: "", contacto: "", equipo: "", ticket: "",
    correo_cliente: "", domicilio: "", localidad: "", ciudad: "",
    telefono: "", actividades: [], itinerario: [],
    calle: "", colonia: "", codigoPostal: "", estado: "",
  });

  const [itinerario, setItinerario] = useState([]);

  useEffect(() => { fetchReserva(); }, [id]);

  const fetchReserva = async () => {
    try {
      const response = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${id}`);
      const data = response.data;
      const itinerarioFormatted = data.itinerario.map((day) => ({
        id: day.id, fecha: new Date(day.fecha.split("T")[0] + "T12:00:00Z"),
        entrada: day.entrada, salida: day.salida, nuevo: false, modifico: false,
      }));
      setReserva(data);
      setFormData({
        status: data.status, empresa: data.empresa, contacto: data.contacto,
        equipo: data.equipo, ticket: data.ticket, correo_cliente: data.correo_cliente || "",
        domicilio: data.domicilio, localidad: data.localidad, ciudad: data.ciudad,
        telefono: data.telefono, actividades: data.actividades, itinerario: itinerarioFormatted,
      });
      setItinerario(itinerarioFormatted);
    } catch (err) { setError("Error al obtener la reserva."); console.error(err); }
    finally { setLoading(false); }
  };

  const handleBuscarTicket = async () => {
    if (!ticketBusqueda.trim()) return alert("Ingresa un numero de ticket.");
    setBuscandoTicket(true);
    try {
      const searchRes = await axios.get(`https://desarrollotecnologicoar.com/api8/search-ticket/${ticketBusqueda}`);
      const found = searchRes.data?.data?.[0];
      if (!found) { alert("No se encontro informacion para ese ticket."); return; }
      const ticketId = found.id;
      const detailRes = await axios.get(`https://desarrollotecnologicoar.com/api8/ticket/${ticketId}`);
      const t = detailRes.data;
      const contacto = t.contactDetails || {};
      const cuenta = t.accountDetails || {};
      const producto = t.productDetails || {};
      const campos = producto.customFields || {};
      const direccionCompleta = `${campos["Calle y Número"] || ""}, ${campos["Colonia"] || ""}, ${campos["Codigo Postal"] || ""}, ${campos["Ciudad"] || ""}, ${campos["Estado"] || ""}`;
      setZohoTicket(t);
      setZohoTicketId(ticketId);
      setFormData((prev) => ({
        ...prev, empresa: cuenta.accountName || "",
        contacto: `${contacto.firstName || ""} ${contacto.lastName || ""}`.trim(),
        correo_cliente: contacto.email || "", telefono: contacto.phone || "",
        equipo: producto.productName || "", ticket: t.ticketNumber || ticketBusqueda,
        domicilio: direccionCompleta.trim(), ciudad: campos["Ciudad"] || "",
        calle: campos["Calle y Número"] || "",
        colonia: campos["Colonia"] || "",
        codigoPostal: campos["Codigo Postal"] || "",
        estado: campos["Estado"] || "",
      }));
      alert("Datos cargados correctamente desde Zoho.");
    } catch (err) { console.error("Error buscando ticket:", err); alert("No se pudo obtener la informacion del ticket."); }
    finally { setBuscandoTicket(false); }
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const ac = place.address_components || [];
        const g = (type) => { const c = ac.find((comp) => comp.types.includes(type)); return c ? c.long_name : ""; };
        setFormData((prev) => ({
          ...prev,
          domicilio: place.formatted_address || "",
          ciudad: g("locality") || g("administrative_area_level_2") || "",
          calle: `${g("route")} ${g("street_number")}`.trim(),
          colonia: g("sublocality_level_1") || g("neighborhood") || "",
          codigoPostal: g("postal_code") || "",
          estado: g("administrative_area_level_1") || "",
        }));
      }
    }
  };

  const handleItinerarioChange = (index, field, value) => {
    setItinerario((prev) => { const updated = [...prev]; updated[index][field] = value; updated[index].modifico = true; return updated; });
  };

  const handleRemoveItinerario = async (index, idBuscado) => {
    if (!window.confirm("¿Eliminar este dia del itinerario?")) return;
    try { await axios.delete(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${idBuscado}`); setItinerario((prev) => prev.filter((_, i) => i !== index)); alert("Dia eliminado correctamente."); }
    catch (error) { console.error("Error al eliminar itinerario:", error); alert("Error al eliminar el dia del itinerario."); }
  };

  const handleAddItinerario = () => {
    setItinerario((prev) => [...prev, { fecha: new Date(), entrada: "", salida: "", nuevo: true, modifico: false }]);
  };

  const handleAgregarNuevo = async (index) => {
    const resultado = itinerario[index];
    if (!resultado) return alert("No se encontro el itinerario.");
    const nuevoItinerario = { reserva_id: id, itinerario: [{ fecha: resultado.fecha, entrada: resultado.entrada, salida: resultado.salida }] };
    try {
      const response = await axios.post("https://desarrollotecnologicoar.com/api5/itinerario_ticket_extension", nuevoItinerario, { headers: { "Content-Type": "application/json" } });
      const nuevoId = response.data.data[0].id;
      setItinerario((prev) => prev.map((day, i) => i === index ? { ...day, id: nuevoId, nuevo: false, modifico: false } : day));
      alert("Itinerario agregado correctamente.");
    } catch (error) { console.error("Error al agregar itinerario:", error); alert("Error al agregar el itinerario."); }
  };

  const handleItinerarioById = async (idBuscado) => {
    const resultado = itinerario.find((item) => item.id === idBuscado);
    if (!resultado) return alert("No se encontro el itinerario.");
    const datosActualizados = { reserva_id: id, fecha: resultado.fecha, entrada: resultado.entrada, salida: resultado.salida };
    try {
      await axios.put(`https://desarrollotecnologicoar.com/api5/itinerario_ticket/${idBuscado}`, datosActualizados, { headers: { "Content-Type": "application/json" } });
      setItinerario((prev) => prev.map((day) => day.id === idBuscado ? { ...day, modifico: false } : day));
      alert("Itinerario actualizado correctamente.");
    } catch (error) { console.error("Error al actualizar itinerario:", error); alert("Error al actualizar el itinerario."); }
  };

  const handleSubmit = async () => {
    if (!formData.ticket || formData.ticket === "SIN_TICKET") {
      alert("Debes cargar los datos del ticket antes de guardar. Usa el botón 'Cargar datos'.");
      return;
    }
    if (itinerario.some((d) => d.nuevo)) {
      alert("Tienes dias nuevos sin guardar. Usa el botón 'Subir' en cada día nuevo antes de continuar.");
      return;
    }
    if (itinerario.some((d) => d.modifico)) {
      alert("Tienes dias con cambios sin guardar. Usa el botón 'Actualizar' en cada día modificado antes de continuar.");
      return;
    }
    try {
      const updatedData = {
        ticketid: zohoTicketId, empresa: formData.empresa, contacto: formData.contacto,
        equipo: formData.equipo, ticket: formData.ticket || reserva?.ticket || "",
        correo_cliente: formData.correo_cliente, domicilio: formData.domicilio,
        ciudad: formData.ciudad, telefono: formData.telefono, localidad: formData.localidad,
        status: "Pendiente",
      };
      await axios.put(`https://desarrollotecnologicoar.com/api5/completar_reservas/${id}`, updatedData, { headers: { "Content-Type": "application/json" } });
      if (zohoTicket?.contactDetails?.id) {
        try {
          await axios.put(`https://desarrollotecnologicoar.com/api8/update-contact/${zohoTicket.contactDetails.id}`, { email: formData.correo_cliente, phone: formData.telefono }, { headers: { "Content-Type": "application/json" } });
        } catch (err) { console.error("Error al actualizar contacto en Zoho:", err); }
      }
      if (zohoTicket?.productDetails?.id) {
        try {
          await axios.put(`https://desarrollotecnologicoar.com/api8/update-family/${zohoTicket.productDetails.id}`, { productCategory: zohoTicket.productDetails.productCategory || "", productName: formData.equipo }, { headers: { "Content-Type": "application/json" } });
          await axios.put(`https://desarrollotecnologicoar.com/api8/update-product/${zohoTicket.productDetails.id}`, { calle: formData.calle, colonia: formData.colonia, ciudad: formData.ciudad, estado: formData.estado, codigoPostal: formData.codigoPostal }, { headers: { "Content-Type": "application/json" } });
        } catch (err) { console.error("Error al actualizar producto en Zoho:", err); }
      }
      alert("Reserva actualizada correctamente.");
      navigate(method === "pendientes" ? "/pendientes" : "/actividad");
    } catch (error) {
      console.error("Error al actualizar la reserva:", error);
      const mensajeError = error.response?.data?.error || "Error al actualizar la reserva.";
      alert(mensajeError);
    }
  };

  const handleCancelar = () => { navigate(method === "pendientes" ? "/pendientes" : "/actividad"); };

  if (loading) return <div className="page-container"><div className="flex items-center justify-center h-64"><svg className="w-8 h-8 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg></div></div>;
  if (error) return <div className="page-container"><div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">{error}</div></div>;

  return (
    <LoadScript googleMapsApiKey="AIzaSyDHkpz3qbH2j6KuFgytJdSNEJp3Pp7XaL0" libraries={libraries}>
      <div className="page-container">
        <h1 className="page-title mb-6">Completar Apartado</h1>

        {/* Ticket Search */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Buscar Ticket en Zoho..." value={ticketBusqueda} onChange={(e) => setTicketBusqueda(e.target.value)} className="input pl-9 py-2 text-sm" />
          </div>
          <button onClick={handleBuscarTicket} disabled={buscandoTicket} className="btn-primary text-sm">
            {buscandoTicket ? "Buscando..." : "Cargar datos"}
          </button>
        </div>

        {/* Form Card */}
        <div className="card p-5 space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">Ticket</label><input type="text" value={formData.ticket} readOnly className="input bg-slate-50" /></div>
            <div><label className="label">Empresa</label><input type="text" value={formData.empresa} readOnly className="input bg-slate-50" /></div>
            <div><label className="label">Contacto</label><input type="text" value={formData.contacto} onChange={(e) => setFormData({ ...formData, contacto: e.target.value })} className="input" /></div>
            <div><label className="label">Correo Cliente</label><input type="email" value={formData.correo_cliente} onChange={(e) => setFormData({ ...formData, correo_cliente: e.target.value })} className="input" /></div>
            <div><label className="label">Equipo</label><input type="text" value={formData.equipo} onChange={(e) => setFormData({ ...formData, equipo: e.target.value })} className="input" /></div>
            <div><label className="label">Telefono</label><input type="tel" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} className="input" /></div>
          </div>
          <div>
            <label className="label">Domicilio</label>
            <Autocomplete onLoad={(ref) => (autocompleteRef.current = ref)} onPlaceChanged={handlePlaceChanged}>
              <input
                type="text"
                value={formData.domicilio}
                onChange={(e) => setFormData({ ...formData, domicilio: e.target.value })}
                className="input"
                placeholder="Escribe una dirección..."
              />
            </Autocomplete>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="label">Ciudad</label><input type="text" value={formData.ciudad} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} className="input" /></div>
            <div>
              <label className="label">Localidad</label>
              <select value={formData.localidad} onChange={(e) => setFormData({ ...formData, localidad: e.target.value })} className="input">
                <option value="Local">Local</option>
                <option value="Foraneo">Foraneo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Itinerario */}
        <h2 className="section-title mb-4">Itinerario</h2>
        <div className="space-y-3 mb-6">
          {itinerario.map((day, index) => (
            <div key={index} className="card p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="label">Fecha</label>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                    <DatePicker value={day.fecha} onChange={(v) => handleItinerarioChange(index, "fecha", v)} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                  </LocalizationProvider>
                </div>
                <div><label className="label">Entrada</label><input type="time" value={day.entrada} onChange={(e) => handleItinerarioChange(index, "entrada", e.target.value)} className="input" /></div>
                <div><label className="label">Salida</label><input type="time" value={day.salida} onChange={(e) => handleItinerarioChange(index, "salida", e.target.value)} className="input" /></div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                {day.nuevo ? (
                  <button onClick={() => handleAgregarNuevo(index)} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1">
                    <HiOutlinePlusCircle className="w-4 h-4" /> Subir
                  </button>
                ) : (
                  <>
                    {day.modifico && (
                      <button onClick={() => handleItinerarioById(day.id)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1 rounded-btn font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors">
                        <HiOutlineArrowPath className="w-4 h-4" /> Actualizar
                      </button>
                    )}
                    <button onClick={() => handleRemoveItinerario(index, day.id)} className="text-xs py-1.5 px-3 inline-flex items-center gap-1 rounded-btn font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors">
                      <HiOutlineTrash className="w-4 h-4" /> Eliminar
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button onClick={handleAddItinerario} className="btn-ghost text-sm">+ Agregar Dia</button>
          <button onClick={handleSubmit} className="btn-primary text-sm">Guardar Cambios</button>
          <button onClick={handleCancelar} className="btn-secondary text-sm">Cancelar</button>
        </div>
      </div>
    </LoadScript>
  );
};

export default CompletarApartado;
